import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, User, Download, Gift, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { StickerImage } from "@/components/StickerImage";
import { PackOpening } from "@/components/PackOpening";
import { api } from "@/lib/api";

// Sezioni fisse dell'album (la Scacchiera/gioco è separata e non fa parte dell'album).
const SECTIONS = [
  { key: "cani", title: "Cani", from: 1, to: 50 },
  { key: "gatti", title: "Gatti", from: 51, to: 100 },
  { key: "armata-cani", title: "Armata Cani", from: 101, to: 116 },
  { key: "armata-gatti", title: "Armata Gatti", from: 117, to: 132 },
];

const pad = (n) => String(n).padStart(3, "0");
const range = (from, to) => Array.from({ length: to - from + 1 }, (_, i) => from + i);

export default function MyAlbum() {
  const navigate = useNavigate();
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const [total, setTotal] = useState(132);
  const [owned, setOwned] = useState([]);
  const [freePackUsed, setFreePackUsed] = useState(false);
  const [unopenedPaidPacks, setUnopenedPaidPacks] = useState(0);
  const [claiming, setClaiming] = useState(false);
  const [openingPaid, setOpeningPaid] = useState(false);
  const [opening, setOpening] = useState(null); // figurine in animazione (già salvate sul server)
  const [lightbox, setLightbox] = useState(null); // numero figurina nel visualizzatore

  const refresh = async () => {
    try {
      const { data } = await api.get("/album/me");
      setOwned(data.owned || []);
      setTotal(data.total || 132);
      setFreePackUsed(!!data.free_pack_used);
      setUnopenedPaidPacks(data.unopened_paid_packs || 0);
    } catch {
      /* non autenticato: gestito dal routing */
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const ownedSet = new Set(owned);
  const progress = owned.length;
  const pct = Math.round((progress / total) * 100);
  const complete = total > 0 && progress >= total;

  // Server-first: l'acquisizione è registrata e persistita PRIMA dell'animazione.
  const claimFreePack = async () => {
    if (claiming) return;
    setClaiming(true);
    try {
      const { data } = await api.post("/album/open-free-pack");
      if (data.newly_opened) {
        setOpening(data.stickers); // avvia animazione (dati già salvati sul server)
      } else {
        await refresh();
        toast.info("Pacchetto gratuito già acquisito.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operazione non riuscita.");
    } finally {
      setClaiming(false);
    }
  };

  const finishOpening = async () => {
    await refresh();
    setOpening(null);
  };

  // Apertura di UN pacchetto a pagamento già accreditato (stesso flusso del gratuito).
  const openPaidPack = async () => {
    if (openingPaid) return;
    setOpeningPaid(true);
    try {
      const { data } = await api.post("/album/open-paid-pack");
      setOpening(data.stickers); // avvia la stessa animazione del pacchetto gratuito
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operazione non riuscita.");
      await refresh();
    } finally {
      setOpeningPaid(false);
    }
  };

  // Download del PDF MASTER completo via endpoint PROTETTO (sessione autenticata).
  // Il percorso interno del MASTER non è mai esposto al client; il server verifica 132/132.
  const [downloadingAlbum, setDownloadingAlbum] = useState(false);
  const downloadFullAlbum = async () => {
    if (downloadingAlbum) return;
    setDownloadingAlbum(true);
    try {
      const { data } = await api.get("/assets/album-complete", { responseType: "blob" });
      const url = window.URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "album-amocaniamogatti-completo.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) toast.error("Completa tutte le 132 figurine per scaricare l'album.");
      else if (status === 401) toast.error("Sessione scaduta: accedi di nuovo.");
      else toast.error("Download non riuscito. Riprova.");
    } finally {
      setDownloadingAlbum(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fdf6ec]" data-testid="album-page">
      {opening && <PackOpening stickers={opening} onComplete={finishOpening} />}

      {lightbox != null && (
        <div
          data-testid="sticker-lightbox"
          className="fixed inset-0 z-40 bg-black/80 flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
        >
          <div
            className="bg-white rounded-3xl p-5 max-w-sm w-full flex flex-col items-center gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full max-h-[60vh] aspect-square flex items-center justify-center overflow-hidden">
              <StickerImage number={lightbox} className="w-full h-full object-contain" />
            </div>
            <span className="text-sm font-bold text-[#2b2622]">Figurina {pad(lightbox)}</span>
            <a
              data-testid="sticker-download-btn"
              href={`${BACKEND}/api/stickers/${lightbox}?download=1`}
              className="inline-flex items-center gap-2 bg-[#e8836b] hover:bg-[#d76f57] text-white rounded-full px-5 py-3 text-sm font-bold transition-colors"
            >
              <Download size={18} /> Scarica originale
            </a>
            <button
              data-testid="sticker-lightbox-close"
              onClick={() => setLightbox(null)}
              className="text-sm text-[#7a6f64]"
            >
              Chiudi
            </button>
          </div>
        </div>
      )}

      <header className="flex items-center justify-between px-6 py-4 max-w-3xl mx-auto">
        <button
          data-testid="album-back-btn"
          onClick={() => navigate("/")}
          aria-label="Torna alla Home"
          className="w-10 h-10 rounded-full bg-white border border-[#f0e4d4] flex items-center justify-center text-[#7a6f64] hover:text-[#c85f45] transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <span className="font-extrabold text-[#2b2622]">IL MIO ALBUM</span>
        <button
          data-testid="account-icon-btn"
          onClick={() => navigate("/account")}
          aria-label="Account"
          className="w-10 h-10 rounded-full bg-white border border-[#f0e4d4] flex items-center justify-center text-[#7a6f64] hover:text-[#c85f45] transition-colors"
        >
          <User size={20} />
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-6 pb-16 flex flex-col gap-8">
        {/* Progresso X/132 + pulsante SCARICA ALBUM (PDF completo ufficiale) */}
        <section
          data-testid="album-progress"
          className="rounded-3xl border border-[#f0e4d4] bg-white p-6 flex flex-col gap-3"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-col">
              <span className="text-sm text-[#7a6f64] font-semibold">Progresso collezione</span>
              <span data-testid="album-progress-count" className="text-3xl font-extrabold text-[#2b2622]">
                {progress}/{total}
              </span>
            </div>
            {complete ? (
              <button
                type="button"
                data-testid="album-download-btn"
                onClick={downloadFullAlbum}
                disabled={downloadingAlbum}
                className="inline-flex items-center gap-2 bg-[#e8836b] hover:bg-[#d76f57] text-white rounded-full px-5 py-3 text-sm font-bold transition-colors disabled:opacity-70"
              >
                <Download size={18} />
                SCARICA ALBUM
              </button>
            ) : (
              <button
                type="button"
                data-testid="album-download-btn"
                disabled
                aria-disabled="true"
                title="Disponibile al completamento della collezione (132/132)"
                className="inline-flex items-center gap-2 bg-[#efe4d6] text-[#b8ab9d] rounded-full px-5 py-3 text-sm font-bold cursor-not-allowed"
              >
                <Download size={18} />
                SCARICA ALBUM
              </button>
            )}
          </div>
          <div className="h-3 rounded-full bg-[#fdeee7] overflow-hidden">
            <div
              className="h-full bg-[#e8836b] transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
        </section>

        {/* Azione: primo pacchetto gratuito (0/132) oppure acquisto (dopo il gratuito) */}
        <section data-testid="album-action" className="flex flex-col items-center gap-2">
          {!freePackUsed ? (
            <Button
              data-testid="free-pack-btn"
              onClick={claimFreePack}
              disabled={claiming}
              className="w-full bg-[#e8836b] hover:bg-[#d76f57] text-white rounded-2xl py-6 px-5 text-sm font-bold leading-snug whitespace-normal h-auto disabled:opacity-60"
            >
              <Gift size={20} className="mr-2 shrink-0" />
              {claiming
                ? "Apertura in corso…"
                : "SCARICA GRATIS IL TUO PRIMO PACCHETTO DI FIGURINE AMOCANIAMOGATTI"}
            </Button>
          ) : unopenedPaidPacks > 0 ? (
            <Button
              data-testid="open-paid-pack-btn"
              onClick={openPaidPack}
              disabled={openingPaid}
              className="w-full bg-[#e8836b] hover:bg-[#d76f57] text-white rounded-2xl py-6 px-5 text-sm font-bold leading-snug whitespace-normal h-auto disabled:opacity-60"
            >
              <Gift size={20} className="mr-2 shrink-0" />
              {openingPaid
                ? "Apertura in corso…"
                : `APRI PACCHETTO${unopenedPaidPacks > 1 ? ` (${unopenedPaidPacks} da aprire)` : ""}`}
            </Button>
          ) : (
            <Button
              data-testid="finish-collection-btn"
              onClick={() => navigate("/")}
              className="w-full bg-[#2b2622] hover:bg-black text-white rounded-2xl py-6 text-sm font-bold"
            >
              <ShoppingCart size={20} className="mr-2 shrink-0" />
              FINISCI LA COLLEZIONE – ACQUISTA
            </Button>
          )}
        </section>

        {/* Sezioni: spazi vuoti numerati; figurine possedute mostrate e scaricabili */}
        {SECTIONS.map((sec) => (
          <section key={sec.key} data-testid={`album-section-${sec.key}`}>
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-lg font-bold text-[#2b2622]">{sec.title}</h2>
              <span className="text-xs text-[#9a8f83]">
                {pad(sec.from)}–{pad(sec.to)}
              </span>
            </div>
            <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-2">
              {range(sec.from, sec.to).map((n) => {
                const filled = ownedSet.has(n);
                return filled ? (
                  <button
                    key={n}
                    data-testid={`album-slot-${pad(n)}`}
                    onClick={() => setLightbox(n)}
                    className="aspect-square rounded-xl overflow-hidden border border-[#e8836b] bg-white hover:ring-2 hover:ring-[#e8836b] transition"
                  >
                    <StickerImage number={n} thumb className="w-full h-full object-contain p-0.5" />
                  </button>
                ) : (
                  <div
                    key={n}
                    data-testid={`album-slot-${pad(n)}`}
                    className="aspect-square rounded-xl flex items-center justify-center text-xs font-semibold border bg-[#faf1e6] border-dashed border-[#e0d4c4] text-[#b8ab9d]"
                  >
                    {pad(n)}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
}
